import api from './api';

/**
 * Get all groups for the user's company connections
 * @returns {Promise} Promise with groups data
 */
export const getGroups = async () => {
  try {
    const response = await api.get('/groups');
    return response.data;
  } catch (error) {
    console.error('Error getting groups:', error);
    throw error;
  }
};

/**
 * Get detailed information about a specific group
 * @param {string} groupId - The group ID
 * @param {number} whatsappId - The WhatsApp connection ID
 * @returns {Promise} Promise with group details
 */
export const getGroupDetails = async (groupId, whatsappId) => {
  try {
    const response = await api.get(`/groups/${groupId}`, {
      params: { whatsappId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting group details:', error);
    throw error;
  }
};

/**
 * Create a new WhatsApp group
 * @param {number} whatsappId - The WhatsApp connection ID
 * @param {string} name - The group name
 * @param {Array<string>} participants - Array of participant phone numbers
 * @returns {Promise} Promise with created group data
 */
export const createGroup = async (whatsappId, name, participants) => {
  try {
    const response = await api.post('/groups', {
      whatsappId,
      name,
      participants
    });
    return response.data;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
};

/**
 * Update a group's name
 * @param {string} groupId - The group ID
 * @param {number} whatsappId - The WhatsApp connection ID
 * @param {string} name - The new group name
 * @returns {Promise} Promise with update result
 */
export const updateGroupName = async (groupId, whatsappId, name) => {
  try {
    const response = await api.put(`/groups/${groupId}/name`, {
      whatsappId,
      name
    });
    return response.data;
  } catch (error) {
    console.error('Error updating group name:', error);
    throw error;
  }
};

/**
 * Update a group's description
 * @param {string} groupId - The group ID
 * @param {number} whatsappId - The WhatsApp connection ID
 * @param {string} description - The new group description
 * @returns {Promise} Promise with update result
 */
export const updateGroupDescription = async (groupId, whatsappId, description) => {
  try {
    const response = await api.put(`/groups/${groupId}/description`, {
      whatsappId,
      description
    });
    return response.data;
  } catch (error) {
    console.error('Error updating group description:', error);
    throw error;
  }
};

/**
 * Update a group's photo
 * @param {string} groupId - The group ID
 * @param {number} whatsappId - The WhatsApp connection ID
 * @param {File} photoFile - The image file
 * @returns {Promise} Promise with update result
 */
export const updateGroupPhoto = async (groupId, whatsappId, photoFile) => {
  try {
    const formData = new FormData();
    formData.append('whatsappId', whatsappId);
    formData.append('photo', photoFile);

    const response = await api.put(`/groups/${groupId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating group photo:', error);
    throw error;
  }
};

/**
 * Leave a WhatsApp group
 * @param {string} groupId - The group ID
 * @param {number} whatsappId - The WhatsApp connection ID
 * @returns {Promise} Promise with leave result
 */
export const leaveGroup = async (groupId, whatsappId) => {
  try {
    const response = await api.delete(`/groups/${groupId}/leave`, {
      data: { whatsappId }
    });
    return response.data;
  } catch (error) {
    console.error('Error leaving group:', error);
    throw error;
  }
};
